import React, { useCallback, useEffect, useRef } from "react";
import styles from "./Carousel.module.scss";
import { Product } from "domains/catalog/aggregates/Product";
import { ICartContext } from "modules/cart/domain/interfaces/IUseCaseCart";
import { useCartStore } from "modules/cart/infra/store/CartStore";
import {
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import { useCatalogStore } from "application/contexts/catalog/CatalogStore";
import { Icon } from "@material-ui/core";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import { useTranslation } from "i18n/useTranslation";

type ScrollDirection = "LEFT" | "RIGHT";

interface CarouselProps {
  products: Product[];
}

const Carousel: React.FC<CarouselProps> = ({ products }) => {
  const { openProductDetail } = useCatalogStore();
  const [addProductCart] = useCartStore((state: ICartContext) => [
    state.addProductCart,
  ]);

  const { resolution } = useScreenStore();
  const { kiosk } = useKiosk();
  const { getProductName } = useTranslation();

  const carouselRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const isDragging = useRef(false);
  const startX = useRef(0);
  const scroll = useRef(0);
  const scrollDirection = useRef<ScrollDirection>("LEFT");

  const stopAutoScroll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startAutoScroll = useCallback(() => {
    stopAutoScroll();
    intervalRef.current = window.setInterval(() => {
      if (carouselRef.current) {
        if (scrollDirection.current === "LEFT") {
          carouselRef.current.scrollLeft += 1;
        } else {
          carouselRef.current.scrollLeft -= 1;
        }

        if (
          carouselRef.current.scrollLeft + carouselRef.current.clientWidth >=
          carouselRef.current.scrollWidth
        ) {
          scrollDirection.current = "RIGHT";
        }

        if (carouselRef.current.scrollLeft <= 0) {
          scrollDirection.current = "LEFT";
        }
      }
    }, 20);
  }, [stopAutoScroll]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      isDragging.current = true;
      if (carouselRef.current) {
        startX.current = e.pageX - carouselRef.current.offsetLeft;
        scroll.current = carouselRef.current.scrollLeft;
      }
      stopAutoScroll();
    },
    [stopAutoScroll]
  );

  const handleMouseLeave = useCallback(() => {
    isDragging.current = false;
    startAutoScroll();
  }, [startAutoScroll]);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      startAutoScroll();
    }, 1000);
  }, [startAutoScroll]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current || !carouselRef.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX.current) * 1; // Velocidade do arrasto
    carouselRef.current.scrollLeft = scroll.current - walk;
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    isDragging.current = true;
    if (carouselRef.current) {
      startX.current = e.touches[0].pageX - carouselRef.current.offsetLeft;
      scroll.current = carouselRef.current.scrollLeft;
    }
    stopAutoScroll();
  }, [stopAutoScroll]);
  
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current || !carouselRef.current) return;
    e.preventDefault();
    const x = e.touches[0].pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX.current) * 1; // Velocidade do arrasto
    carouselRef.current.scrollLeft = scroll.current - walk;
  }, []);
  
  const handleTouchEnd = useCallback(() => {
    isDragging.current = false;
  
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      startAutoScroll();
    }, 1000);
  }, [startAutoScroll]);

  useEffect(() => {
    startAutoScroll();

    return () => stopAutoScroll();
  }, [startAutoScroll, stopAutoScroll]);

  const onImgError = (ev: React.ChangeEvent<HTMLImageElement>) => {
    ev.target.src = require("../../../../../assets/images/no-image.png");
  };

  const hasComposition = useCallback((product?: Product) => {
    if (product && product.compositions?.length) {
      if (product.compositions.some((it) => it.compositionItems.length > 0)) {
        return true;
      } else if (product?.cartRecommendationByProduct.length && product.cartRecommendationByProduct.length > 0) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }, []);

  const onClickAdd = useCallback(
    (product: Product) => {
      if (hasComposition(product) || product.productGeneralObservation) {
        openProductDetail(product.id);
      } else {
        addProductCart(product, 1, [], product.adultHood);
      }
    },
    [addProductCart, hasComposition, openProductDetail]
  );

  return (
    <div
      className={styles.carousel}
      ref={carouselRef}
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className={styles.items}>
        {products.map((item, index) => (
          <div
            key={index}
            className={styles.item}
            style={{
              width: resolution === ResolutionType.FULL_HD ? 200 : 130,
            }}
            onClick={() => onClickAdd(item)}
          >
            <img
              className={styles.image}
              style={{
                width: resolution === ResolutionType.FULL_HD ? 150 : 100,
                height: resolution === ResolutionType.FULL_HD ? 150 : 100,
              }}
              onErrorCapture={onImgError}
              src={item.thumbnailUrl}
              alt="product"
            />

            <div
              style={{
                margin:
                  resolution === ResolutionType.FULL_HD ? "16px 0" : "8px 0",
              }}
            >
              <span>{getProductName(item)}</span>
              <div
              >
                <Icon
                  style={{
                    fontSize: kiosk?.isMee ? 30 : 20,
                  }}
                >
                  add
                </Icon>
              </div>
            </div>
            <div>
              <span
                className={item.discount ? styles.discountValue : undefined}
              >
                {item.price > 0 ? (
                  <>
                    <span
                      style={{
                        fontSize:
                          resolution === ResolutionType.FULL_HD ? 28 : 20,
                      }}
                    >
                      {`R$ ${item.price.toFixed(2).replace(".", ",")}`}
                    </span>
                  </>
                ) : (
                  <></>
                )}
              </span>
              {item.discount && (
                <span style={{ marginLeft: -30 }}>
                  &nbsp;R${" "}
                  {(item.price - item.price * (item.discount / 100))
                    .toFixed(2)
                    .replace(".", ",")}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Carousel;
